import { useTranslation } from 'react-i18next'
import { useMemo, useRef, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Bar } from 'react-chartjs-2'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { v4 as uuidv4 } from 'uuid'
import PropTypes from 'prop-types'

import GraphContainer from '../../GraphContainer'
import { hasChartData } from '../helpers/chartDataHelpers'
import CustomAxis from './CustomAxis'

import { gridCharts, helpTexts } from '../../../constants'

import { getOptions } from './options'
import { getCustomDataLabelsOptions } from '../../reusableGraphOptions'
import { getHtmlLegendPlugin } from '../../../../plugins'
import { generateHeadcountData, getDataLabelsSize, dataLabelsStyles, createDatasets, getMaxValues } from './helpers'
import { useSroiFilters } from '../../../SroiFiltersContext'
import { useSroiReportCurrencySymbol } from '../../../hooks'
import { useChartExportMode } from '../../../hooks/useChartExportMode'
import { useExportAnimationCallback } from '../../../hooks/useExportAnimationCallback'
import { customContainerStyles, getCustomBoxSpansTemplate } from './customBoxSpan'

import { socialReturnOnInvestmentReportSelector } from '../../../../../../modules/selectors/socialReturnOnInvestment'

import useStyles, { chartColors } from './styles'

const HeadcountAndPerStakeholderImpact = ({ isExportCopy, onChartReady, onExportStart, onExportEnd }) => {
  const { t } = useTranslation()

  const chartRef = useRef(null)
  const [chartAreaDimensions, setChartAreaDimensions] = useState(null)

  const currencySymbol = useSroiReportCurrencySymbol()
  const { selectedRegion } = useSroiFilters()
  const report = useSelector(socialReturnOnInvestmentReportSelector)

  // Track export mode (PDF or PNG) using centralized hook
  const { isPdfGenerating } = useChartExportMode()

  // Handle animation completion callback for PNG export
  // (PDF export doesn't need signaling - chart stays in grid and scrollable elements are expanded synchronously)
  const handleAnimationComplete = useExportAnimationCallback({
    isExportCopy,
    onChartReady,
    isPdfGenerating
  })

  const chartData = useMemo(() => {
    return generateHeadcountData({
      selectedRegion,
      report
    })
  }, [selectedRegion, report])

  // Calculate scroll settings based on number of stakeholders
  // During export (PDF or PNG): disable scrolling and let chart expand to full height
  const { isScrollable, maxHeight, chartHeight } = useMemo(() => {
    const stakeholderCount = chartData?.labels?.length || 0
    const MAX_VISIBLE_STAKEHOLDERS = 6
    const HEIGHT_PER_STAKEHOLDER = 30 // pixels per bar/row
    const BASE_PADDING = 120 // padding for axes, titles, etc.

    const fullChartHeight = stakeholderCount * HEIGHT_PER_STAKEHOLDER + BASE_PADDING

    // For export (PNG copy or PDF), disable scrolling and maxHeight constraint
    if (isExportCopy || isPdfGenerating) {
      return {
        isScrollable: false,
        maxHeight: null,
        chartHeight: fullChartHeight
      }
    }

    // Normal display logic
    if (stakeholderCount > MAX_VISIBLE_STAKEHOLDERS) {
      return {
        isScrollable: true,
        maxHeight: MAX_VISIBLE_STAKEHOLDERS * HEIGHT_PER_STAKEHOLDER + BASE_PADDING,
        chartHeight: fullChartHeight
      }
    }

    return {
      isScrollable: false,
      maxHeight: null,
      chartHeight: fullChartHeight
    }
  }, [chartData, isExportCopy, isPdfGenerating])

  const classes = useStyles({ isScrollable, maxHeight, chartHeight })

  const { maxHeadcount, maxPerStakeholder } = useMemo(() => {
    return getMaxValues(chartData.headcountData, chartData.perStakeholderData)
  }, [chartData])

  // Calculate appropriate label size based on data count
  const dataLabelsSize = useMemo(() => {
    const dataSetsAmount = chartData?.labels?.length || 0
    return getDataLabelsSize(dataSetsAmount)
  }, [chartData])

  // Get style properties for the calculated size
  const dataLabelStyles = dataLabelsStyles[dataLabelsSize]

  const impactGraphDatasets = useMemo(
    () =>
      createDatasets({
        chartData,
        chartColors,
        dataLabelStyles,
        maxHeadcount,
        maxPerStakeholder,
        getCustomDataLabelsOptions,
        currencySymbol
      }),
    [chartData, maxHeadcount, maxPerStakeholder, dataLabelStyles, currencySymbol]
  )

  const graphData = useMemo(
    () => ({
      labels: chartData.labels,
      datasets: impactGraphDatasets
    }),
    [impactGraphDatasets, chartData.labels]
  )

  const legendContainerId = useMemo(() => uuidv4(), [])

  const options = useMemo(() => {
    const baseOptions = getOptions(legendContainerId, currencySymbol, isScrollable)

    // Add animation callback for export modes (PNG or PDF)
    // PNG export: signals to exportChartAsPng via onChartReady
    // PDF export: signals to PdfExportContext via signalComponentReady
    if ((isExportCopy && onChartReady) || (isPdfGenerating && !isExportCopy)) {
      return {
        ...baseOptions,
        animation: {
          ...(baseOptions.animation || {}),
          onComplete: handleAnimationComplete
        }
      }
    }

    return baseOptions
  }, [
    legendContainerId,
    currencySymbol,
    isScrollable,
    isExportCopy,
    onChartReady,
    isPdfGenerating,
    handleAnimationComplete
  ])

  const customBoxSpansTemplate = useMemo(() => {
    return getCustomBoxSpansTemplate(currencySymbol)
  }, [currencySymbol])

  const plugins = useMemo(() => {
    const htmlLegendPlugin = getHtmlLegendPlugin(customContainerStyles, customBoxSpansTemplate)

    return [htmlLegendPlugin, ChartDataLabels]
  }, [customBoxSpansTemplate])

  // Capture chart area dimensions to align custom axes with the actual plotting area
  // (needed because Chart.js canvas includes both Y-axis labels and data area)
  useEffect(() => {
    if (chartRef.current && isScrollable) {
      const chart = chartRef.current
      if (chart.chartArea) {
        setChartAreaDimensions({
          left: chart.chartArea.left,
          right: chart.chartArea.right,
          width: chart.chartArea.right - chart.chartArea.left
        })
      }
    }
  }, [chartData, isScrollable])

  // Don't render chart if there's no data
  if (!hasChartData(graphData)) {
    return null
  }

  const containerId = isExportCopy
    ? `${gridCharts.sroiHeadcountPerStakeholder}-export`
    : gridCharts.sroiHeadcountPerStakeholder

  return (
    <GraphContainer
      id={containerId}
      title={t('Headcount and Per Stakeholder Impact')}
      className={classes.graphContainer}
      onExportStart={onExportStart}
      onExportEnd={onExportEnd}
      helpText={helpTexts.headcountAndPerStakeholderImpact}
    >
      {/* Custom axes stay fixed outside the scrollable canvas when there are >6 stakeholders */}
      {isScrollable && (
        <CustomAxis
          color={chartColors.perStakeholderImpact}
          maxValue={maxPerStakeholder}
          position="top"
          currencySymbol={currencySymbol}
          chartAreaDimensions={chartAreaDimensions}
        />
      )}
      <div className={classes.chartWrapper}>
        <div className={classes.chartCanvas}>
          <Bar ref={chartRef} type="bar" data={graphData} options={options} plugins={plugins} />
        </div>
      </div>
      {isScrollable && (
        <CustomAxis
          color={chartColors.headcount}
          maxValue={maxHeadcount}
          position="bottom"
          chartAreaDimensions={chartAreaDimensions}
        />
      )}
      <div className={classes.legendContainer} id={legendContainerId}>
        {/* The legend of the graph will be rendered here by the plugin */}
      </div>
    </GraphContainer>
  )
}

HeadcountAndPerStakeholderImpact.propTypes = {
  isExportCopy: PropTypes.bool,
  onChartReady: PropTypes.func,
  onExportStart: PropTypes.func,
  onExportEnd: PropTypes.func
}

export default HeadcountAndPerStakeholderImpact
